/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaSetupNameDialog.tsx
 * FILE_ID: JOBHUB-QA-SETUP-NAME-001
 * PURPOSE: Ask once where a new QA goes, then a name only for new or replace.
 * Reused from: packages/job-hub/src/jobs/tabs/job-qa/QaWriteTargetDialog.tsx (QaWriteTargetDialog)
 */
import { useEffect, useState } from 'react'
import { useIsMdUp } from '@quotech/ui'

export type QaSetupNameAction = 'create' | 'add' | 'replace'

export function JobQaSetupNameDialog({
  selectedName,
  pending,
  onCancel,
  onConfirm,
}: {
  selectedName: string
  pending: boolean
  onCancel: () => void
  onConfirm: (action: QaSetupNameAction, name: string) => void
}) {
  const tap = useIsMdUp() ? '' : 'min-h-[44px]'
  const [step, setStep] = useState<'choose' | 'name'>('choose')
  const [action, setAction] = useState<'create' | 'replace'>('create')
  const [name, setName] = useState('')
  const canUseSelected = selectedName.trim().length > 0

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const trimmed = name.trim()
  const openName = (next: 'create' | 'replace') => {
    setAction(next)
    setName(next === 'replace' ? selectedName : '')
    setStep('name')
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-background/70 p-4"
      role="presentation"
      onClick={onCancel}
    >
      <form
        role="dialog"
        aria-labelledby="qa-setup-name-title"
        className="w-full max-w-sm rounded-xl border border-border bg-card p-4 shadow-lg"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault()
          if (step !== 'name' || !trimmed || pending) return
          onConfirm(action, trimmed)
        }}
      >
        <h2 id="qa-setup-name-title" className="text-sm font-semibold text-foreground">
          {step === 'name' ? 'QA name' : 'Where should this QA go?'}
        </h2>
        {step === 'choose' ? (
          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => openName('create')}
              className={`rounded-md border border-border px-3 text-sm font-medium ${tap || 'py-2'}`}
            >
              Add as a new QA
            </button>
            <button
              type="button"
              disabled={!canUseSelected || pending}
              onClick={() => onConfirm('add', '')}
              className={`rounded-md border border-border px-3 text-sm font-medium disabled:opacity-50 ${tap || 'py-2'}`}
            >
              Add to selected QA
            </button>
            <button
              type="button"
              disabled={!canUseSelected}
              onClick={() => openName('replace')}
              className={`rounded-md border border-border px-3 text-sm font-medium disabled:opacity-50 ${tap || 'py-2'}`}
            >
              Replace selected QA
            </button>
          </div>
        ) : (
          <>
            <label className="mt-3 block text-sm text-foreground" htmlFor="qa-setup-name">
              Name
            </label>
            <input
              id="qa-setup-name"
              value={name}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              className={`mt-1 w-full rounded-md border border-border bg-background px-3 text-sm ${tap || 'py-1.5'}`}
            />
          </>
        )}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {step === 'name' ? (
            <button
              type="button"
              onClick={() => setStep('choose')}
              className={`rounded-md px-2.5 py-1.5 text-xs text-muted-foreground ${tap}`}
            >
              Back
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            className={`rounded-md px-2.5 py-1.5 text-xs text-muted-foreground ${tap}`}
          >
            Cancel
          </button>
          {step === 'name' ? (
            <button
              type="submit"
              disabled={!trimmed || pending}
              className={`rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50 ${tap}`}
            >
              Continue
            </button>
          ) : null}
        </div>
      </form>
    </div>
  )
}
